import { Key, useCallback, useEffect, useRef, useState } from 'react';
import { Tab, Tabs, useDisclosure } from '@heroui/react';
import { LOCAL_STORAGE_LESSON_STEPS_VIEW } from '@/shared/constants/localStorage';
import type { IContentStep } from '@/shared/types/lesson';
import {
  LessonSingleStep,
  LessonStepContent,
  type LessonStepScormMode,
} from './LessonStepContent';
import { LessonStepPager } from './LessonStepPager';
import { SessionStepsDrawer } from './SessionStepsDrawer';

type LessonStepsProps<TStep extends IContentStep> = {
  steps: TStep[];
  scormMode: LessonStepScormMode;
  onScormAction: (step: TStep) => void;
  scormLoadingStepId?: number | null;
};

type StepsView = 'single' | 'all';

const getStepElementId = (order: number) => `lesson-step-${order}`;

const readStepsView = (): StepsView => {
  try {
    return localStorage.getItem(LOCAL_STORAGE_LESSON_STEPS_VIEW) === 'all' ? 'all' : 'single';
  } catch {
    return 'single';
  }
};

const saveStepsView = (view: StepsView) => {
  try {
    localStorage.setItem(LOCAL_STORAGE_LESSON_STEPS_VIEW, view);
  } catch {
    // недоступный localStorage не мешает переключению
  }
};

export const LessonSteps = <TStep extends IContentStep>({
  steps,
  scormMode,
  onScormAction,
  scormLoadingStepId,
}: LessonStepsProps<TStep>) => {
  const { isOpen: isDrawerOpen, onOpen: openDrawer, onClose: closeDrawer } = useDisclosure();
  const [selectedOrder, setSelectedOrder] = useState<number | null>(null);
  const [view, setView] = useState<StepsView>(readStepsView);
  const stepsTopRef = useRef<HTMLDivElement>(null);

  const activeStepOrder =
    selectedOrder !== null && steps.some((step) => step.order === selectedOrder)
      ? selectedOrder
      : (steps[0]?.order ?? null);

  const handleChangeView = useCallback((key: Key) => {
    const nextView = key as StepsView;
    setView(nextView);
    saveStepsView(nextView);
  }, []);

  const goToStep = useCallback(
    (order: number) => {
      setSelectedOrder(order);
      if (view === 'single') {
        stepsTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      const target = document.getElementById(getStepElementId(order));
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    [view],
  );

  useEffect(() => {
    if (view !== 'all') return undefined;

    const elements = steps
      .map((step) => document.getElementById(getStepElementId(step.order)))
      .filter((el): el is HTMLElement => Boolean(el));

    if (!elements.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (!visible) return;
        const order = Number((visible.target as HTMLElement).dataset.lessonStepOrder);
        if (Number.isFinite(order)) setSelectedOrder(order);
      },
      {
        root: null,
        rootMargin: '-20% 0px -55% 0px',
        threshold: [0.15, 0.35, 0.55],
      },
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [steps, view]);

  if (activeStepOrder === null) return null;

  if (steps.length === 1) {
    const [step] = steps;
    return (
      <LessonSingleStep
        step={step}
        scormMode={scormMode}
        onScormAction={() => onScormAction(step)}
        isScormLoading={scormLoadingStepId === step.id}
      />
    );
  }

  const visibleSteps =
    view === 'single' ? steps.filter((step) => step.order === activeStepOrder) : steps;

  return (
    <>
      <div ref={stepsTopRef} className="flex scroll-mt-28 flex-col gap-y-6">
        <div className="flex justify-end">
          <Tabs
            aria-label="Steps view"
            variant="light"
            selectedKey={view}
            onSelectionChange={handleChangeView}
            classNames={{
              cursor: 'bg-black',
              tabContent: 'group-data-[selected=true]:text-white',
            }}
          >
            <Tab key="single" title="One step" />
            <Tab key="all" title="All steps" />
          </Tabs>
        </div>
        {view === 'single' && (
          <LessonStepPager
            steps={steps}
            currentStepOrder={activeStepOrder}
            onChangeStep={goToStep}
          />
        )}
      </div>
      <div className="flex flex-col gap-y-6">
        {visibleSteps.map((step) => (
          <LessonStepContent
            key={step.id}
            step={step}
            stepId={getStepElementId(step.order)}
            scormMode={scormMode}
            onScormAction={() => onScormAction(step)}
            isScormLoading={scormLoadingStepId === step.id}
          />
        ))}
      </div>
      <SessionStepsDrawer
        isOpen={isDrawerOpen}
        onOpen={openDrawer}
        onClose={closeDrawer}
        steps={steps}
        currentStepOrder={activeStepOrder}
        onSelectStep={goToStep}
      />
    </>
  );
};
